import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function AccountSettings() {
  const { user, applySession } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error("Inserisci la password attuale");
      return;
    }
    if (!newEmail && !newPassword) {
      toast.error("Inserisci una nuova email o una nuova password");
      return;
    }
    if (newPassword && newPassword.length < 8) {
      toast.error("La nuova password deve avere almeno 8 caratteri");
      return;
    }
    if (newPassword && newPassword !== confirmPassword) {
      toast.error("Le due password non coincidono");
      return;
    }
    setLoading(true);
    try {
      const payload = { current_password: currentPassword };
      if (newEmail) payload.new_email = newEmail;
      if (newPassword) payload.new_password = newPassword;
      const { data } = await api.put("/auth/account", payload);
      applySession(data.access_token, data.user);
      toast.success("Credenziali aggiornate correttamente");
      setCurrentPassword("");
      setNewEmail("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="h-5 w-5 text-primary" />
        <h2 className="font-serif text-xl font-bold">Impostazioni account</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Account attuale: <span className="font-medium text-foreground">{user?.email}</span>. Modifica
        email e/o password della redazione. Serve sempre la password attuale.
      </p>
      <form onSubmit={submit} className="space-y-4" data-testid="account-form">
        <div>
          <Label htmlFor="cur-pass">Password attuale *</Label>
          <Input id="cur-pass" type="password" autoComplete="current-password" value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)} className="rounded-sm mt-1.5"
            data-testid="account-current-password" />
        </div>
        <div className="border-t border-border pt-4">
          <Label htmlFor="new-email">Nuova email (facoltativa)</Label>
          <Input id="new-email" type="email" value={newEmail} placeholder={user?.email}
            onChange={(e) => setNewEmail(e.target.value)} className="rounded-sm mt-1.5"
            data-testid="account-new-email" />
        </div>
        <div>
          <Label htmlFor="new-pass">Nuova password (facoltativa, min 8 caratteri)</Label>
          <Input id="new-pass" type="password" autoComplete="new-password" value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)} className="rounded-sm mt-1.5"
            data-testid="account-new-password" />
        </div>
        <div>
          <Label htmlFor="conf-pass">Conferma nuova password</Label>
          <Input id="conf-pass" type="password" autoComplete="new-password" value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)} className="rounded-sm mt-1.5"
            data-testid="account-confirm-password" />
        </div>
        <Button type="submit" disabled={loading} className="rounded-sm gap-1.5" data-testid="account-save-btn">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Salva modifiche
        </Button>
      </form>
    </div>
  );
}
